import getB from "../utils/bridges/EkaBridge";
import { ensurePagifyLoaded } from "../utils/pagifySingeltonInstance";
import {
  DateAndTimeConfigForReceipt,
  TCreateReceiptResponse,
  TGetReceiptPdfConfig,
} from "@elixir/types";
import { MyAccessibleClinic } from "../utils/bridges/types/doctorProfile";
import { TReceipt } from "../types/appointment";
import {
  finalVisitDateRenderer,
  safeReturnOfNumber,
} from "../utils/receipt-utils";
import { returnActorNamePerformingWithPersona } from "../utils/bridges/methods";
import { roundToTwoDecimals } from "@elixir/utils";

export const PAGIFY_FAILURE_FALLBACK_STRING = `Pagify PDF generation failed`;
export type MassagedDoctorProfile = {
  personal: {
    name: string;
  };
  professional: {
    header_text: string | undefined;
  };
};
export type ReceiptObjectForPdf = TReceipt & {
  config: TGetReceiptPdfConfig;
  uhid: string | undefined;
  visit_date: string;
  doctor: MassagedDoctorProfile;
  clinicData: MyAccessibleClinic;
  gender: string | undefined;
  ageInM: number | undefined;
  totalDiscountOnEntireBill: number;
};
export const generateReceiptPDFOnClient = async ({
  receiptData,
  pdfConfigForGeneration,
  patientData,
  doctorProfile,
}: {
  receiptData: TCreateReceiptResponse;
  pdfConfigForGeneration?: TGetReceiptPdfConfig;
  patientData: TCreateReceiptResponse["patientData"];
  doctorProfile: TCreateReceiptResponse["doctorProfile"];
}) => {
  const bridge = getB();
  const receiptUtils = bridge.templar.receipt;
  let pdfObject: ReceiptObjectForPdf | undefined;

  const config =
    pdfConfigForGeneration || receiptUtils.getDefaultPdfConfigForReceipt();

  const dateTimeRenderinFormat =
    config?.flags?.date_and_time || DateAndTimeConfigForReceipt.DATE_ONLY;

  const additionalDiscount = safeReturnOfNumber(
    receiptData?.additional_discount_amount
  );
  const totalDiscountAcrossServices = safeReturnOfNumber(
    receiptData?.sku_discount_amount
  );
  const billCreatedByConfig = config.flags?.print_bill_created_by || false;

  pdfObject = {
    config: config,
    receipt_id: receiptData.receipt_id,
    uhid: patientData?.uhid,
    ...(config?.flags?.bill_created_at && {
      bill_created_at: finalVisitDateRenderer(
        dateTimeRenderinFormat,
        (receiptData?.updated_at || receiptData?.created_at) as string
      ),
    }),
    visit_date: finalVisitDateRenderer(
      dateTimeRenderinFormat,
      receiptData?.Appointment?.full_date // epoch
    ),
    doctor: {
      personal: {
        name: doctorProfile?.name,
      },
      professional: {
        header_text: doctorProfile?.headerText,
      },
    },
    clinicData: doctorProfile?.clinicObject,
    mobile: receiptData?.mobile,
    name: receiptData?.name,
    receipt_amount: receiptData?.receipt_amount,
    receipt_number: receiptData?.receipt_number || "",
    business_receipt_number: receiptData?.business_receipt_number,
    additional_discount_amount: additionalDiscount,
    additional_discount_type: receiptData?.additional_discount_type,
    additional_discount_value: receiptData?.additional_discount_value,
    net_amount: receiptData?.net_amount,
    receipt_sku: receiptData?.receipt_sku,
    receipt_payment: receiptData?.receipt_payment,
    amount_due: receiptData?.amount_due,
    receipt_type: receiptData?.receipt_type,
    ref_trx_id: receiptData?.ref_trx_id,
    payment_status: receiptData?.payment_status || "success",
    gender: patientData?.gender,
    ageInM: patientData?.age,
    totalDiscountOnEntireBill: roundToTwoDecimals(
      additionalDiscount + totalDiscountAcrossServices
    ),
    ...(config.flags?.print_amount_in_words && {
      receiptAmountinWords: receiptData?.receiptAmountinWords,
    }),
    ...(billCreatedByConfig && {
      billCreatedBy: returnActorNamePerformingWithPersona(),
    }),
  };

  try {
    await ensurePagifyLoaded();

    const { flags, heights, margins, page_size = "A4" } = config;
    const { print_header, print_footer } = flags;

    const doctorName = doctorProfile.name;

    const bodyHTML =
      pdfObject?.receipt_type === "DUE_PAYMENT"
        ? receiptUtils.getBodyForPaymentNoteForReceipt({ data: pdfObject })
        : receiptUtils.getBodyForReceipt({ data: pdfObject! });

    const headerHTML = print_header
      ? receiptUtils.getHeaderForReceipt({
          doctorName,
          headerText: pdfObject?.doctor?.professional?.header_text || "",
          clinicName: pdfObject?.clinicData?.name || "",
          clinicAddress: pdfObject?.clinicData?.address?.line1 || "",
          config,
          data: pdfObject,
        })
      : "";

    const footerHTML = print_footer
      ? receiptUtils.getFooterForReceipt({
          config,
          data: pdfObject,
        })
      : "";

    const headHTML = receiptUtils.getPdfCssForReceipt(pdfObject);

    const pdfBlob = await new Promise<{
      success: boolean;
      blob?: Blob;
      pdfBlobUrl?: string;
    }>((resolve, reject) => {
      // @ts-ignore
      window?.pagify?.render({
        head_html: headHTML,
        body_html: bodyHTML,
        header_html: headerHTML,
        footer_html: footerHTML,
        page_size: page_size,
        margin_left: `${0}cm`,
        margin_right: `${0}cm`,
        header_height: `${heights?.header || 2.5}cm`,
        footer_height: `${heights?.footer + 1 || 10.6}cm`,
        onPdfReady: async (blobUrl: string) => {
          try {
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            resolve({
              success: true,
              blob,
              pdfBlobUrl: blobUrl,
            });
          } catch (error) {
            reject({
              success: false,
            });
          }
        },
        onPdfError: (error: string) => {
          const mergedErrorWithConfig = new Error(
            `${PAGIFY_FAILURE_FALLBACK_STRING}: ${error}`
          );
          (mergedErrorWithConfig as any).pdfObject = pdfObject;
          reject(mergedErrorWithConfig);
        },
      });
    });
    return pdfBlob;
  } catch (e) {
    if (e instanceof Error && !(e as any).pdfObject && pdfObject) {
      (e as any).pdfObject = pdfObject;
    }
    throw e;
  }
};
